/**
 * hydrateComponents — mounts React component islands into placeholder elements
 * that the TemplateEngine injected into the DOM during HTML string rendering.
 *
 * Flow:
 *   1. TemplateEngine.renderTree() → HTML string with <div data-react-id="…"> placeholders
 *   2. Caller sets container.innerHTML = html
 *   3. Caller calls tplEngine.hydrateInto(container)
 *      → internally calls hydrateComponents(container, mountQueue)
 *      → each placeholder is found, a React 18 root is created, and the
 *        component is rendered with its props + rendered child HTML.
 *
 * Cleanup:
 *   Call unmountComponents(roots) before re-rendering to avoid memory leaks.
 */

import React from "react"
import { createRoot } from "react-dom/client"

/**
 * Mounts all queued React components into their placeholder <div> elements.
 *
 * @param {HTMLElement} container  - The DOM node whose innerHTML was set by the engine.
 * @param {Array<Object>} mountQueue - Mount descriptors accumulated during renderTree().
 * @param {string}            mountQueue[].id           - Unique placeholder ID.
 * @param {React.ComponentType} mountQueue[].component  - Component to render.
 * @param {Object}            mountQueue[].props        - Props forwarded to the component.
 * @param {string}            mountQueue[].childrenHtml - Rendered HTML of nested template children.
 * @returns {Array<import("react-dom/client").Root>} React roots — pass to unmountComponents() for cleanup.
 */
export function hydrateComponents(container, mountQueue) {
    if (!container || !Array.isArray(mountQueue) || mountQueue.length === 0) {
        return []
    }

    const roots = []

    for (const { id, component: Component, props, childrenHtml } of mountQueue) {
        const placeholder = container.querySelector(`[data-react-id="${id}"]`)
        if (!placeholder) continue

        // Nested template children are passed as a raw-HTML child element so
        // React components can slot them in via {children} or render them
        // anywhere they like via dangerouslySetInnerHTML.
        const children =
            childrenHtml
                ? React.createElement("div", {
                      "data-tpl-children": "true",
                      dangerouslySetInnerHTML: { __html: childrenHtml },
                  })
                : null

        const element = React.createElement(Component, props, children)

        try {
            const root = createRoot(placeholder)
            root.render(element)
            roots.push(root)
        } catch (err) {
            console.error(
                `[TemplateEngine] hydrateComponents: failed to mount "${id}"`,
                err,
            )
        }
    }

    return roots
}

/**
 * Unmounts all React roots created by a previous hydrateComponents() call.
 * Must be called before re-rendering the same container to prevent memory leaks.
 *
 * @param {Array<import("react-dom/client").Root>} roots - List returned by hydrateComponents().
 */
export function unmountComponents(roots) {
    if (!Array.isArray(roots)) return
    for (const root of roots) {
        try {
            root.unmount()
        } catch (_) {
            // Silently ignore — root may have already been unmounted
        }
    }
}
